import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth, googleProvider, db, setAnalyticsUser, trackEvent } from '../firebase';
import { User, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { clearPendingReferralCode, getPendingReferralCode, resolveReferralCode } from '../util/referralUtils';

// Extended user type that includes Firestore fields
type ExtendedUser = User & {
  credits?: number;
  name?: string;
  bio?: string;
  imageUrl?: string;
};

const AuthContext = createContext<any | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Function to fetch Firestore user data
  const fetchUserData = async (authUser: User) => {
    try {
      const userDocRef = doc(db, 'users', authUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        // Combine auth user with Firestore data
        const userData = userDoc.data();
        return {
          ...authUser,
          ...userData
        } as ExtendedUser;
      }
      
      return authUser as ExtendedUser;
    } catch (error) {
      console.error("Error fetching user data:", error);
      return authUser as ExtendedUser;
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      if (authUser) {
        // Get user with Firestore data
        const extendedUser = await fetchUserData(authUser);
        setUser(extendedUser);
        void setAnalyticsUser(authUser.uid);
      } else {
        setUser(null);
        void setAnalyticsUser(null);
      }
      setLoading(false);
    });
    
    return unsubscribe;
  }, []);

  const login = async (email: string, password: string) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const extendedUser = await fetchUserData(result.user);
    setUser(extendedUser);
    void trackEvent('login', { method: 'password' });
    return result;
  };

  const register = async (email: string, password: string) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    // Create initial user document in Firestore
    await setDoc(doc(db, 'users', result.user.uid), {
      name: result.user.displayName || email.split('@')[0],
      email: result.user.email,
      createdAt: new Date(),
      credits: 0,
    });
    
    const extendedUser = await fetchUserData(result.user);
    setUser(extendedUser);
    void trackEvent('sign_up', { method: 'password' });
    return result;
  };

  const loginWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const authUser = result.user;
      
      // Check if user exists in Firestore
      const userDocRef = doc(db, 'users', authUser.uid);
      const userDoc = await getDoc(userDocRef);
      const isNewUser = !userDoc.exists();
      
      if (!userDoc.exists()) {
        const pendingCode = getPendingReferralCode();
        let referredBy: string | null = null;
        if (pendingCode) {
          try {
            const referrerUid = await resolveReferralCode(pendingCode);
            if (referrerUid && referrerUid !== authUser.uid) {
              referredBy = referrerUid;
            }
          } catch (err) {
            console.error('Failed to resolve referral code:', err);
          }
        }

        const userData: Record<string, any> = {
          name: authUser.displayName,
          email: authUser.email,
          createdAt: new Date(),
          credits: 0,
        };
        if (referredBy && pendingCode) {
          userData.referredBy = referredBy;
          userData.referredByCode = pendingCode;
        }

        await setDoc(userDocRef, userData);
        clearPendingReferralCode();
      }
      
      // Get combined user data
      const extendedUser = await fetchUserData(authUser);
      setUser(extendedUser);
      void trackEvent(isNewUser ? 'sign_up' : 'login', { method: 'google' });
      
      return result;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    void setAnalyticsUser(null);
    void trackEvent('logout');
  };

  const value = {
    user,
    setUser,
    loading,
    login,
    register,
    loginWithGoogle,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
